import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, Plus, Search } from 'lucide-react';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { toApiError } from '@shared/api/errors';
import { cn } from '@shared/lib/cn';
import { fetchCouriers } from '../memberAppApi';
import { CARD, HELP } from '../visitorAccessStyles';

interface CourierSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
  onPick: (courier: string) => void;
}

/** "Pick the courier", over the whole screen as the mock has it: the gate's own list to search, or the name as typed. */
export function CourierSheet({ open, onOpenChange, locationId, onPick }: CourierSheetProps): JSX.Element {
  // The search mounts with each open, so every open starts empty.
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-paper"
        >
          {/* The page header's look; Back closes the screen and returns to the form. */}
          <header className="sticky top-0 flex items-center gap-3 bg-primary-800 px-3.5 pb-3 pt-safe-or-3">
            <Dialog.Close aria-label="Back" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/25 text-white">
              <ArrowLeft className="h-4.5 w-4.5" strokeWidth={2} />
            </Dialog.Close>
            <Dialog.Title className="m-0 text-[21px] font-semibold text-white">Pick the courier</Dialog.Title>
          </header>
          <CourierSearch locationId={locationId} onPick={onPick} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CourierSearch({ locationId, onPick }: { locationId: string; onPick: (courier: string) => void }): JSX.Element {
  const [search, setSearch] = useState('');
  const couriersQuery = useQuery({ queryKey: ['access-couriers', locationId], queryFn: () => fetchCouriers(locationId) });

  // 1. What was typed can always be used as is, even when the gate's list didn't load.
  const typed = search.trim();
  const matches = (couriersQuery.data ?? []).filter((name) => name.toLowerCase().includes(typed.toLowerCase()));
  const rowClass = 'flex min-h-[52px] w-full items-center gap-2.5 px-4 text-left text-[15px]';

  return (
    <div className="flex flex-col gap-3.5 p-4">
      <label className={cn(CARD, 'flex min-h-[46px] items-center gap-2 rounded-[23px] px-3.5')}>
        <Search className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} />
        <input
          aria-label="Search couriers"
          placeholder="Search couriers"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-ink-muted"
        />
      </label>

      {couriersQuery.isError ? (
        <Banner
          variant="danger"
          title="Couldn't load the gate's list"
          description={`${toApiError(couriersQuery.error).message} You can still type the courier's name above and use it.`}
        />
      ) : null}

      {/* 2. The typed name first, then the gate's couriers that match it. */}
      {typed || matches.length ? (
        <div className={cn(CARD, 'flex flex-col divide-y divide-hairline overflow-hidden')}>
          {typed ? (
            <button type="button" className={cn(rowClass, 'font-semibold text-primary-700')} onClick={() => onPick(typed)}>
              <Plus className="h-4 w-4 shrink-0" strokeWidth={2.2} />
              Use "{typed}"
            </button>
          ) : null}
          {matches.map((name) => (
            <button key={name} type="button" className={cn(rowClass, 'font-medium text-ink')} onClick={() => onPick(name)}>
              {name}
            </button>
          ))}
        </div>
      ) : null}
      {couriersQuery.isLoading ? <Skeleton className="h-40 rounded-lg" /> : null}

      <p className={HELP}>This list comes from the gate system, so the guard sees the same name.</p>
    </div>
  );
}
